import * as React from 'react'
import { Heading, Span } from '@/components/Text'
import { PortableText } from '@portabletext/react'
import styled from 'styled-components'
import {
  Product,
  ShopifyStorefrontProductVariant,
  ShopifyStorefrontSelectedOption,
} from '@/types'
import {
  ProductDetailsWrapper,
  ProductHeader,
  ProductBody,
  OptionWrapper,
} from './styles'
import { formatPrice } from '@/utils/formatting'
import { BuyButton } from './BuyButton'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'

interface ProductDetailsProps {
  product: Product
  selectedVariant: ShopifyStorefrontProductVariant
  selectVariant: (
    selectedOptions: Omit<ShopifyStorefrontSelectedOption, '__typename'>[],
  ) => void
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  border: '1px solid black',
  borderTop: 0,
  borderRadius: '0',
  width: '100%',
  display: 'flex',
  columnGap: 0,
  '& .MuiToggleButtonGroup-root': {
    borderRadius: 0,
  },
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    padding: 9,
    flex: 1,
    height: '62px',
    borderRight: '1px solid black',
    borderRadius: '0',
    fontFamily: 'var(--serif)',
    fontSize: '32px',
    textTransform: 'none',
    color: 'black',
    backgroundColor: 'lightGray',
    marginRight: 1,
    '&:last-child': {
      borderRight: 0,
      marginRight: 0,
    },
    '&.Mui-disabled': {
      border: 0,
    },
  },
}))

export const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  selectedVariant,
  selectVariant,
}) => {
  const { shopify, sanity } = product
  const { options } = shopify
  const currentVariantOptions = selectedVariant.selectedOptions
  const getCurrentOptionValue = (name: string) => {
    const option = currentVariantOptions.find((o) => o.name === name)
    if (option) {
      return option.value
    }
    console.warn(`Could not get option with name "${name}"`, {
      currentVariantOptions,
    })
  }

  const handleSelectChange =
    (optionName: string) =>
    (event: React.MouseEvent<HTMLElement, MouseEvent>, value: any) => {
      const newValue = value
      const newOptions = currentVariantOptions.map((option) => {
        if (option.name !== optionName) return option
        return {
          name: optionName,
          value: newValue,
        }
      })
      selectVariant(newOptions)
    }

  return (
    <ProductDetailsWrapper>
      <ProductHeader>
        <Heading level="h1" textTransform={'uppercase'}>
          {shopify.title}
        </Heading>
        <Span className="pdp-price">
          {formatPrice(selectedVariant.priceV2)}
        </Span>
      </ProductHeader>
      <ProductBody>
        <PortableText value={sanity.body} />
      </ProductBody>
      {options
        .filter((o) => o.name != 'Title')
        .map((option) => (
          <OptionWrapper key={option.id}>
            <div className="preview">
              {option.name == 'Color' ? (
                <div
                  className="color-swatch"
                  id={getCurrentOptionValue(option.name)
                    ?.split(' ')[0]
                    .toLowerCase()}
                />
              ) : (
                <span>{getCurrentOptionValue(option.name)}</span>
              )}
              <p>{option.name}</p>
            </div>
            <StyledToggleButtonGroup
              id={option.id}
              className="options-group"
              value={getCurrentOptionValue(option.name)}
              exclusive
              onChange={handleSelectChange(option.name)}
              aria-label={option.name}
              sx={{
                borderRadius: 0,
              }}
            >
              {option.values.map((optionValue) => (
                <ToggleButton
                  fullWidth={true}
                  key={optionValue}
                  title={optionValue}
                  value={optionValue}
                  id={optionValue?.split(' ')[0].toLowerCase()}
                >
                  {option.name == 'Color' ? null : optionValue}
                </ToggleButton>
              ))}
            </StyledToggleButtonGroup>
          </OptionWrapper>
        ))}
      <BuyButton product={shopify} selectedVariant={selectedVariant} />
    </ProductDetailsWrapper>
  )
}
